/**
 * The logged-in top bar's OVERFLOW RULE (kitecorp/transferify-ui#451, amended by #452) — which pill
 * items give up their place in the bar, and at which viewport widths.
 *
 * The pill is a flex row in an `position:absolute`, background-less topbar: it has no scroller and no
 * wrap, so anything wider than the row is simply clipped by the viewport edge with no gesture that
 * reveals it. Before this rule the bar overflowed at EVERY width below ~807px — the icon-only band
 * (≤453px) pushed the ProfileMenu avatar itself off-screen, and that avatar is the only route to
 * Account, Support, Language and Log out.
 *
 * The owner's decision (2026-08-06) was to COLLAPSE rather than scroll: below a width threshold the
 * lower-traffic items move into the ProfileMenu as ordinary entries, the way ui#117 moved Language
 * there. Home and Transfers are the highest-traffic destinations and never collapse; Resources, the
 * theme toggle and the avatar are the bar's fixed furniture and never collapse either.
 *
 * The owner then amended it for Personalization (2026-08-08, ui#452): on a PHONE it is worth a slot
 * in the bar, as the icon-only 44px square every other pill item becomes below 640px, rather than a
 * popover row. So its collapse is a BAND rather than a ceiling — see {@link NAV_COLLAPSE_RANGE}.
 *
 * This lives outside React (epic #26 / `logic-outside-react`) so the thresholds are one testable
 * table rather than numbers scattered across two components' media queries, and so the pill copy and
 * the menu copy of an item are provably driven by COMPLEMENTARY queries — an item that could be
 * hidden in both places at once would be unreachable, which is the very defect this rule removes.
 */

/** A pill item that can move into the ProfileMenu, in the order the bar shows them. */
export type CollapsibleNavItem = "contacts" | "branding";

/**
 * BAR order. Branding is listed here because it collapses, not because it collapses first — its band
 * means it is BACK in the pill at the narrowest widths (at 375px the collapsed set is Contacts alone).
 *
 * Reminders was the third item until kitecorp/transferify-ui#3140 moved file requests under Transfers
 * and took Reminders out of the bar. Its removal only frees room, so the other two bounds stand as
 * measured; what it did change is that some widths now collapse nothing at all (421–640px), which is why
 * the ProfileMenu's section follows {@link navCollapseSectionHiddenCss} rather than one threshold.
 */
export const COLLAPSIBLE_NAV_ITEMS: ReadonlyArray<CollapsibleNavItem> = ["contacts", "branding"];

/**
 * The widths at which an item is NOT in the pill.
 *
 * Every bound is a MAX-width, never a min-width, and that is deliberate: `min-width: N+1px` leaves a
 * hole at fractional viewport widths (browser zoom, some Windows DPI scales), and a width landing in
 * that hole would hide the item in the pill AND in the menu, making it unreachable. Expressing the
 * lower bound of a band as "except at or below X" keeps every query a `max-width` or its exact
 * negation, which cannot have a hole.
 */
export interface NavCollapseRange {
	/** Collapsed at and below this width … */
	readonly maxWidth: number;
	/** … except at and below this one, where the item is back in the pill (ui#452). */
	readonly exceptAtOrBelow?: number;
}

/**
 * Each bound is measured against the real logged-in bar in Chromium, in BOTH catalogs — the Romanian
 * destination labels ("Transferuri") are the longest the app ships, so they set the text-mode numbers.
 * `e2e/nav-overflow.spec.ts` is the committed re-measurement.
 *
 * PROVENANCE (ui#451), measured while Reminders ("Mementouri") was still in the bar, which ui#3140 removed.
 * "Viewport it needs" already includes the brand mark and the topbar's own padding, so these are
 * whole-bar figures, not pill figures; every row also carries Resources, the theme toggle and the avatar,
 * which never collapse:
 *
 * | pill contents (text mode)                          | pill width | viewport it needs |
 * |----------------------------------------------------|-----------:|------------------:|
 * | Home Transfers Contacts Reminders Personalization  |      613.6 |             806.9 |
 * | Home Transfers Contacts Reminders                  |      529.8 |             723.1 |
 * | Home Transfers Contacts (at 641px)                 |      417.4 |             610.7 |
 *
 * ui#451 set each bound to those figures plus ~25–35px, "so the pill never touches the brand mark".
 * That is where REMINDERS' 759 comes from (723.1 + 36), and it is the rule to re-derive from if the
 * TEXT-mode pill changes again. ui#451's icon-mode rows are superseded by the arithmetic under
 * CONTACTS below, which is the same measurement retaken with Personalization in the pill.
 *
 * ui#452 could NOT keep that ~25px rule in the icon band, and the loosening is deliberate rather than
 * an oversight worth hiding: with Personalization permanently in the icon-only pill, 375px leaves
 * 13px of clearance and no threshold can buy more, because below Home + Transfers + Personalization
 * there is nothing left to collapse. 13px is therefore the icon band's floor, and the CONTACTS bound
 * is chosen so that no width is WORSE than the one width that cannot be helped. The text-mode bounds
 * keep the original ~25px rule untouched.
 *
 * CONTACTS — 420, raised from ui#451's 389 because the icon-only pill now permanently carries
 * Personalization too (ui#452). Below 641px the pill is icon-only and its width is CONSTANT, so this
 * is arithmetic rather than judgement: the pill is 328px with Contacts and 282px without, the brand
 * mark occupies 62px and the topbar keeps 18px of padding, so the clearance between brand mark and
 * pill is `viewport − 408` with Contacts and `viewport − 362` without. At 375px — the narrowest
 * viewport the bar is designed for — the second figure leaves 13px, so that is the tightest clearance
 * the ui#452 decision permits anywhere; Contacts rejoins the pill only once it can do so on the same
 * terms, i.e. from 421px up. Left at 389 the pill's right edge lands exactly ON the viewport edge at
 * 390px (measured), which is a Pixel 8 and one pixel off an iPhone 16.
 *
 * REMINDERS — was 759 (723.1 + 36, the TEXT-mode row with Reminders in it) until ui#3140 removed the item.
 *
 * BRANDING (Personalization) — collapsed only between 641px and 879px, because both of its bounds are
 * forced from opposite directions:
 *
 *  - the LABELLED form, the only one that fits alongside labelled destinations, needs the full bar.
 *    Pill right edge minus viewport (negative = fits; −30 is the topbar's full 30px padding):
 *
 *    | viewport | English | Romanian |
 *    |---------:|--------:|---------:|
 *    |      800 |     +10 |       +6 |
 *    |      810 |       0 |       −4 |
 *    |      820 |     −10 |      −14 |
 *    |      840 |     −30 |      −30 |
 *
 *    So it overflows below ~810 and reaches the bar's full padding at 840 — NOT the ~780 the ui#452
 *    report estimated from an English-only measurement taken before ui#451 landed.
 *
 *    Those figures are macOS, and they are the reason this bound is 879 and not ui#451's 839. The
 *    SAME measurement on CI's Linux font stack renders the labelled pill ~13px wider, so 840 leaves
 *    an inset of 17px (English) / 20px (Romanian) instead of 30 — the bar eating its own padding.
 *    Full padding therefore arrives at 853 on the stricter platform, and 879 carries the ~25px the
 *    other bounds in this table carry. 839 had zero headroom on the machine it was measured on and
 *    was already 13px short on the other, which no check could see until `nav-overflow.spec.ts`
 *    started asserting the INSET rather than "did not overflow" (ui#452).
 *
 *  - below 641px none of that applies: the wordmark is gone, the padding halves, the separator is
 *    dropped and every item is a 44px square, so the whole question changes and the owner's answer is
 *    that Personalization keeps a square of its own. Hence `exceptAtOrBelow: 640` — the SAME
 *    breakpoint at which the other pill items go icon-only, so the item collapses exactly where a
 *    label would have to be rendered and nowhere else.
 */
export const NAV_COLLAPSE_RANGE: Record<CollapsibleNavItem, NavCollapseRange> = {
	contacts: {maxWidth: 420},
	branding: {maxWidth: 879, exceptAtOrBelow: 640},
};

/**
 * The widest bound in the table: above it every item is back in the bar, so no width above it ever
 * hands the ProfileMenu anything. The feedback edge tab reads it as "the widths a phone or a small
 * tablet reaches Support from the menus" (`FeedbackEdgeTab.styles`).
 *
 * It is NOT the section's own query any more (kitecorp/transferify-ui#3140): below it the collapsed
 * widths now have a gap (421–640px collapse nothing since Reminders left the bar), so one threshold would
 * show an empty group and its orphan divider there. The section follows
 * {@link navCollapseSectionHiddenCss}, derived from every item's own range.
 */
export const NAV_COLLAPSE_SECTION_MAX_WIDTH: number =
	Math.max(...COLLAPSIBLE_NAV_ITEMS.map(item => NAV_COLLAPSE_RANGE[item].maxWidth));

/** Whether an item has left the pill for the ProfileMenu at this viewport width. */
export function isNavItemCollapsed(item: CollapsibleNavItem, viewportWidth: number): boolean {
	const {maxWidth, exceptAtOrBelow} = NAV_COLLAPSE_RANGE[item];
	if (viewportWidth > maxWidth) {
		return false;
	}
	return exceptAtOrBelow === undefined || viewportWidth > exceptAtOrBelow;
}

/** The items the ProfileMenu hosts at this viewport width, in bar order. */
export function collapsedNavItems(viewportWidth: number): CollapsibleNavItem[] {
	return COLLAPSIBLE_NAV_ITEMS.filter(item => isNavItemCollapsed(item, viewportWidth));
}

/**
 * Narrows a destination key (or any pill item's identity) to a collapsible one, so the bar can ask
 * "does this item collapse?" without restating the membership list at the render site.
 */
export function collapsibleNavItem(key: string): CollapsibleNavItem | undefined {
	return COLLAPSIBLE_NAV_ITEMS.find(item => item === key);
}

/** The media query that matches exactly the widths at or below `maxWidth`. */
export function collapsedMedia(maxWidth: number): string {
	return `@media (max-width: ${maxWidth}px)`;
}

/**
 * The exact logical NEGATION of {@link collapsedMedia}.
 *
 * `not all and (max-width: Npx)` rather than `(min-width: N+1px)` on purpose: the `+1px` form leaves
 * a hole at fractional viewport widths (browser zoom, some Windows DPI scales), and a width landing
 * in that hole would hide an item in the pill AND in the menu, making it unreachable — the exact
 * failure this whole rule exists to prevent. Negating the query cannot have a hole.
 */
export function expandedMedia(maxWidth: number): string {
	return `@media not all and (max-width: ${maxWidth}px)`;
}

/** Wraps `body` in `queries`, outermost first. Nested `@media` is an AND of its conditions. */
function nestMedia(queries: ReadonlyArray<string>, body: string): string {
	return queries.reduceRight((inner, query) => `${query} { ${inner} }`, body);
}

/**
 * CSS that hides the PILL copy of an item at exactly the widths it is collapsed.
 *
 * A band becomes NESTED media queries rather than one `and`-joined query, because the lower bound is
 * a negation (`not all and (max-width: 640px)`) and CSS has no way to `and` a negated query inline at
 * this level — nesting is the AND. Both are Media Queries Level 3, so no `(not …)` support question
 * arises.
 */
export function pillHiddenCss(item: CollapsibleNavItem): string {
	const {maxWidth, exceptAtOrBelow} = NAV_COLLAPSE_RANGE[item];
	const queries = exceptAtOrBelow === undefined
		? [collapsedMedia(maxWidth)]
		: [collapsedMedia(maxWidth), expandedMedia(exceptAtOrBelow)];
	return nestMedia(queries, "display: none;");
}

/**
 * CSS that hides the MENU copy of an item at exactly the widths it is NOT collapsed — the exact
 * complement of {@link pillHiddenCss}, so the item is in one surface or the other at every width and
 * never in both or neither.
 *
 * De Morgan is why this is a LIST of rules rather than one: the complement of `A and B` is
 * `(not A) or (not B)`, and CSS spells "or" as separate rules that set the same declaration.
 */
export function menuRowHiddenCss(item: CollapsibleNavItem): string {
	const {maxWidth, exceptAtOrBelow} = NAV_COLLAPSE_RANGE[item];
	const rules = [nestMedia([expandedMedia(maxWidth)], "display: none;")];
	if (exceptAtOrBelow !== undefined) {
		rules.push(nestMedia([collapsedMedia(exceptAtOrBelow)], "display: none;"));
	}
	return rules.join(" ");
}

/**
 * One width bound of a media query, as DATA: the same condition can then be written as CSS for the
 * browser and evaluated for a given width in a test, so the section's rule is proven by what it does
 * rather than by the text it generates.
 */
interface WidthBound {
	/** True for "at or below `width`" ({@link collapsedMedia}), false for "above it" ({@link expandedMedia}). */
	readonly atOrBelow: boolean;
	readonly width: number;
}

/** When `item` is in the PILL, as an OR of AND-ed bounds: the complement of its collapsed range. */
function expandedClauses(item: CollapsibleNavItem): WidthBound[][] {
	const {maxWidth, exceptAtOrBelow} = NAV_COLLAPSE_RANGE[item];
	const clauses: WidthBound[][] = [[{atOrBelow: false, width: maxWidth}]];
	if (exceptAtOrBelow !== undefined) {
		clauses.push([{atOrBelow: true, width: exceptAtOrBelow}]);
	}
	return clauses;
}

/**
 * When NO item is collapsed: every item's "in the pill" condition AND-ed together. Each condition is an
 * OR, so the AND is distributed over them into an OR of AND-ed bounds, one rule per combination.
 */
function sectionHiddenClauses(): WidthBound[][] {
	return COLLAPSIBLE_NAV_ITEMS.reduce<WidthBound[][]>(
		(combined, item) => combined.flatMap(clause => expandedClauses(item).map(extra => [...clause, ...extra])),
		[[]],
	);
}

function holds(bound: WidthBound, viewportWidth: number): boolean {
	return bound.atOrBelow ? viewportWidth <= bound.width : viewportWidth > bound.width;
}

function mediaOf(bound: WidthBound): string {
	return bound.atOrBelow ? collapsedMedia(bound.width) : expandedMedia(bound.width);
}

/** Whether the ProfileMenu's collapsed section is hidden at this width: exactly when it holds no row. */
export function isNavCollapseSectionHidden(viewportWidth: number): boolean {
	return sectionHiddenClauses().some(clause => clause.every(bound => holds(bound, viewportWidth)));
}

/**
 * CSS that hides the ProfileMenu's collapsed SECTION (the group and its divider) at exactly the widths
 * where no item is collapsed, so the popover never shows an empty group or an orphan rule.
 *
 * Derived from every item's range rather than from {@link NAV_COLLAPSE_SECTION_MAX_WIDTH}: that single
 * threshold was only right while every width below it collapsed something, which stopped being true
 * when Reminders left the bar (kitecorp/transferify-ui#3140) and 421–640px began to collapse nothing.
 */
export function navCollapseSectionHiddenCss(): string {
	return sectionHiddenClauses().map(clause => nestMedia(clause.map(mediaOf), "display: none;")).join(" ");
}
